import { z } from "zod"

import { revisionSchema } from "./sync.ts"

/**
 * 1. Trip detection & capture source (PRD R4.2, ADR-013).
 */
export const tripSourceSchema = z.enum(["manual", "phone_gps", "beacon", "hardware"])
export type TripSource = z.infer<typeof tripSourceSchema>

/**
 * 2. Tax classification category (PRD R4.6).
 */
export const tripCategorySchema = z.enum(["unclassified", "business", "private"])
export type TripCategory = z.infer<typeof tripCategorySchema>

/**
 * 3. Free-tier quota visibility (ADR-015, PRD R6.7).
 */
export const tripVisibilitySchema = z.enum(["full", "masked"])
export type TripVisibility = z.infer<typeof tripVisibilitySchema>

/**
 * 4. Offline sync hold reason (ADR-015): `quota` = free-tier limit, `upgrade` = the feature is not
 * in the plan (UPGRADE_REQUIRED); both are replayed once the entitlement changes.
 */
export const holdReasonSchema = z.enum(["quota", "upgrade"])
export type HoldReason = z.infer<typeof holdReasonSchema>

/** Former name of `holdReasonSchema`, kept as an alias. */
export const tripHoldReasonSchema = holdReasonSchema
export type TripHoldReason = HoldReason

/**
 * 5. Data quality flags (PRD R4.16 - R4.19).
 */
export const tripQualityFlagSchema = z.enum([
  "straight_line_gap",
  "auto_ended",
  "possible_commute",
  "odometer_mismatch",
  "overlaps_other_trip",
  // The detector cut a stretch faster than road speed (plane, train); sync carries it unchanged.
  "non_road_trimmed",
  // The vehicle selected when the drive ended was deleted before the trip was filed, so the trip was put under the
  // current choice instead; the review asks the owner to confirm the vehicle (T87). Set by the native clients.
  "vehicle_deleted",
])
export type TripQualityFlag = z.infer<typeof tripQualityFlagSchema>

/**
 * 6. Derived review state enum (PRD R4.13, Decisions U6).
 * NOT a stored database column; dynamically computed from ended_at, reviewed_at and category.
 * A business trip's purpose is optional (Decisions E2/G1, T152): it never affects the state.
 */
export const tripReviewStateSchema = z.enum(["needs_review", "confirmed"])
export type TripReviewState = z.infer<typeof tripReviewStateSchema>

/**
 * Pure function to dynamically derive the review state for a trip record.
 */
export function deriveTripReviewState(trip: {
  ended_at: string | null
  reviewed_at: string | null
  category: TripCategory
}): TripReviewState {
  if (trip.ended_at === null) {
    return "needs_review"
  }
  if (trip.reviewed_at === null) {
    return "needs_review"
  }
  if (trip.category === "unclassified") {
    return "needs_review"
  }
  return "confirmed"
}

/**
 * Who wrote a trip's `start_place` / `end_place` (T134): the platform geocoder or the user.
 * A client may overwrite geocoded places; user-typed ones are never replaced automatically.
 */
export const tripPlaceSourceSchema = z.enum(["geocoder", "user"])
export type TripPlaceSource = z.infer<typeof tripPlaceSourceSchema>

/** Longest `start_place` / `end_place` a request may carry (characters). */
export const TRIP_PLACE_MAX_LENGTH = 80

/**
 * A place as a request states it (T134): suburb plus state such as "Parramatta NSW", never a
 * street or number. Trimmed; blank is not a place, clients send null instead. The stored
 * schema below is lenient (plain string) so one odd row can never break list or sync.
 */
export const tripPlaceRequestSchema = z.string().trim().min(1).max(TRIP_PLACE_MAX_LENGTH)

/**
 * Full trip entity schema as stored in database and returned over API.
 */
/** How a trip's odometer values were produced (vehicle-odometer-policy.md section 2). */
export const odometerOriginSchema = z.enum(["manual", "gps_derived"])
export type OdometerOrigin = z.infer<typeof odometerOriginSchema>

export const tripSchema = z.object({
  id: z.uuid(),
  organisation_id: z.uuid(),
  vehicle_id: z.uuid(),
  /** Who recorded the trip (ADR-016 2.4); null on trips from before Teams. Lenient and optional (stored schema). Emitted on pull and push answers since T1383 (additive; the native readers already tolerate it, ADR-017 2.2 rule 6); only on rows the viewer may already read. */
  recorded_by_user_id: z.string().nullable().optional(),
  source: tripSourceSchema,
  started_at: z.string().datetime({ offset: true }),
  started_tz: z.string(),
  ended_at: z.string().datetime({ offset: true }).nullable(),
  distance_m: z.number().int(),
  start_odometer_km: z.number().int().nullable(),
  end_odometer_km: z.number().int().nullable(),
  odometer_origin: odometerOriginSchema.nullable().default(null),
  category: tripCategorySchema,
  // Suburb + state ("Parramatta NSW"); null until resolved or typed. Lenient: no length rule.
  start_place: z.string().nullable().default(null),
  end_place: z.string().nullable().default(null),
  place_source: tripPlaceSourceSchema.nullable().default(null),
  purpose: z.string().nullable(),
  reviewed_at: z.string().datetime({ offset: true }).nullable(),
  start_estimated: z.boolean(),
  end_estimated: z.boolean(),
  start_gap_m: z.number().int().nullable(),
  start_lat: z.number().nullable(),
  start_lon: z.number().nullable(),
  end_lat: z.number().nullable(),
  end_lon: z.number().nullable(),
  points_count: z.number().int(),
  quality_flags: z.array(tripQualityFlagSchema),
  visibility: tripVisibilitySchema,
  hold_reason: tripHoldReasonSchema.nullable(),
  /**
   * Manual correction of a closed phone_gps trip (T666). `distance_m`, `started_at` and `ended_at`
   * above always hold the EFFECTIVE value (logbook, reports, odometer read them unchanged). The first
   * correction copies the GPS values to the three `gps_*` evidence fields; they never change again.
   * `corrected_at`/`corrected_by_user_id`/`correction_reason` describe the latest correction.
   * All null on a trip that was never corrected. Lenient: no limits on a stored row.
   */
  gps_distance_m: z.number().int().nullable().default(null),
  gps_started_at: z.string().datetime({ offset: true }).nullable().default(null),
  gps_ended_at: z.string().datetime({ offset: true }).nullable().default(null),
  corrected_at: z.string().datetime({ offset: true }).nullable().default(null),
  corrected_by_user_id: z.string().nullable().default(null),
  correction_reason: z.string().nullable().default(null),
  // Kept for the 5-year ATO record but out of the active logbook. Same convention as
  // vehicles.archived_at: null means "active", a timestamp means the user archived it.
  archived_at: z.string().datetime({ offset: true }).nullable(),
  revision: revisionSchema,
  deleted_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})

export type Trip = z.infer<typeof tripSchema>

/**
 * Longest `correction_reason` a request may carry, in Unicode code points (zod 4 counts code
 * points, so 200 emoji are valid). Clients count and cut the same way: see `trip-correction-rules.ts`.
 */
export const TRIP_CORRECTION_REASON_MAX_LENGTH = 200

/**
 * Coordinates a request may carry (T144). The stored `tripSchema` stays lenient (plain numbers)
 * so one odd legacy row can never break list or sync; requests are strict.
 */
const latitudeRequestSchema = z.number().min(-90).max(90)
const longitudeRequestSchema = z.number().min(-180).max(180)

/**
 * A coordinate pair is both present or both null (T144): half a position cannot be drawn,
 * measured from or compared. The issue lands on the field that is missing.
 */
function addHalfPairIssue(
  ctx: z.RefinementCtx,
  lat: number | null | undefined,
  lon: number | null | undefined,
  latField: string,
  lonField: string,
): void {
  if ((lat === null || lat === undefined) === (lon === null || lon === undefined)) return
  const missing = lat === null || lat === undefined ? latField : lonField
  ctx.addIssue({
    code: "custom",
    message: `${latField} and ${lonField} must be sent together, both numbers or both null`,
    path: [missing],
  })
}

/**
 * Request payload to create a new trip record.
 */
export const createTripRequestSchema = z
  .object({
    id: z.uuid().optional(),
    vehicle_id: z.uuid(),
    source: tripSourceSchema.default("phone_gps"),
    started_at: z.string().datetime({ offset: true }),
    started_tz: z.string().min(1).default("Australia/Sydney"),
    ended_at: z.string().datetime({ offset: true }).nullable().optional().default(null),
    distance_m: z.number().int().nonnegative().default(0),
    start_odometer_km: z.number().int().nonnegative().nullable().optional().default(null),
    end_odometer_km: z.number().int().nonnegative().nullable().optional().default(null),
    odometer_origin: odometerOriginSchema.nullable().default(null),
    category: tripCategorySchema.default("unclassified"),
    start_place: tripPlaceRequestSchema.nullable().optional().default(null),
    end_place: tripPlaceRequestSchema.nullable().optional().default(null),
    // Omitted: the server derives it ("user" when a place is present). Null: no source.
    place_source: tripPlaceSourceSchema.nullable().optional(),
    purpose: z.string().nullable().optional().default(null),
    reviewed_at: z.string().datetime({ offset: true }).nullable().optional().default(null),
    start_estimated: z.boolean().default(false),
    end_estimated: z.boolean().default(false),
    start_gap_m: z.number().int().nonnegative().nullable().optional().default(null),
    start_lat: latitudeRequestSchema.nullable().optional().default(null),
    start_lon: longitudeRequestSchema.nullable().optional().default(null),
    end_lat: latitudeRequestSchema.nullable().optional().default(null),
    end_lon: longitudeRequestSchema.nullable().optional().default(null),
    points_count: z.number().int().nonnegative().default(0),
    quality_flags: z.array(tripQualityFlagSchema).default([]),
    visibility: tripVisibilitySchema.default("full"),
    hold_reason: tripHoldReasonSchema.nullable().optional().default(null),
    archived_at: z.string().datetime({ offset: true }).nullable().optional().default(null),
  })
  .superRefine((trip, ctx) => {
    addHalfPairIssue(ctx, trip.start_lat, trip.start_lon, "start_lat", "start_lon")
    addHalfPairIssue(ctx, trip.end_lat, trip.end_lon, "end_lat", "end_lon")
  })

export type CreateTripRequest = z.input<typeof createTripRequestSchema>
export type ParsedCreateTripRequest = z.infer<typeof createTripRequestSchema>

/**
 * Request payload to update a trip's review decision (category and purpose).
 * Endpoint: PATCH /api/v1/trips/:id/review
 */
export const updateTripReviewRequestSchema = z.object({
  base_revision: z.number().int().nonnegative().optional(),
  category: tripCategorySchema,
  purpose: z.string().nullable().optional().default(null),
})

export type UpdateTripReviewRequest = z.infer<typeof updateTripReviewRequestSchema>

/**
 * Request payload to update an existing trip.
 */
export const updateTripRequestSchema = z
  .object({
    base_revision: z.number().int().nonnegative().optional(),
    // Explicit lifecycle intents. They are not an "archived" boolean because the client
    // only ever states an intent, never sets a timestamp: the server stamps the time.
    archived_at: z.string().datetime({ offset: true }).nullable().optional(),
    vehicle_id: z.uuid().optional(),
    category: tripCategorySchema.optional(),
    /**
     * Places (T134). Changing either without `place_source` makes the server record "user"
     * (or null when both end up empty); a geocoder write states `place_source: "geocoder"`.
     */
    start_place: tripPlaceRequestSchema.nullable().optional(),
    end_place: tripPlaceRequestSchema.nullable().optional(),
    place_source: tripPlaceSourceSchema.nullable().optional(),
    purpose: z.string().nullable().optional(),
    reviewed_at: z.string().datetime({ offset: true }).nullable().optional(),
    /**
     * Manual trips, or an open phone_gps trip being finished (T173), may change `started_at`;
     * a closed phone_gps trip rejects changes with VALIDATION_FAILED (gps_trip_time_immutable).
     */
    started_at: z.string().datetime({ offset: true }).optional(),
    ended_at: z.string().datetime({ offset: true }).nullable().optional(),
    distance_m: z.number().int().nonnegative().optional(),
    start_odometer_km: z.number().int().nonnegative().nullable().optional(),
    end_odometer_km: z.number().int().nonnegative().nullable().optional(),
    odometer_origin: odometerOriginSchema.nullable().optional(),
    points_count: z.number().int().nonnegative().optional(),
    quality_flags: z.array(tripQualityFlagSchema).optional(),
    /** The start and end positions each move as a pair (T144, T173): both numbers, or both null. */
    start_lat: latitudeRequestSchema.nullable().optional(),
    start_lon: longitudeRequestSchema.nullable().optional(),
    start_estimated: z.boolean().optional(),
    /** Distance from the corrected start to the first fix; same rule as create (T563). */
    start_gap_m: z.number().int().nonnegative().nullable().optional(),
    end_lat: latitudeRequestSchema.nullable().optional(),
    end_lon: longitudeRequestSchema.nullable().optional(),
    end_estimated: z.boolean().optional(),
    /**
     * Manual correction intent (T666), same pattern as `archived_at`: the client states a time, the
     * server stamps its own `corrected_at` and `corrected_by_user_id`. Only with this field may a
     * CLOSED automatically captured trip (any `source` except `manual`, see `isCorrectableTripSource`)
     * change `started_at`, `ended_at` or `distance_m`; the server then keeps the
     * GPS values in `gps_*`, returns the trip to the review inbox and records the reason.
     * The `gps_*` evidence and `corrected_by_user_id` are server-owned and not accepted here.
     */
    corrected_at: z.string().datetime({ offset: true }).optional(),
    correction_reason: z.string().trim().min(1).max(TRIP_CORRECTION_REASON_MAX_LENGTH).optional(),
  })
  .superRefine((patch, ctx) => {
    addHalfPairIssue(ctx, patch.start_lat, patch.start_lon, "start_lat", "start_lon")
    addHalfPairIssue(ctx, patch.end_lat, patch.end_lon, "end_lat", "end_lon")
  })

export type UpdateTripRequest = z.infer<typeof updateTripRequestSchema>
